import { useState } from 'react';
import { useInView } from '../hooks/useInView';
import { destinations, moreDestinations } from '../data/mock';
import { MapPin, ArrowRight } from 'lucide-react';

export const DestinationsSection = () => {
  const [ref, isInView] = useInView();
  const [hoveredIndex, setHoveredIndex] = useState(null);

  return (
    <section id="destinations" className="relative py-24 bg-[#0A1628]">
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.015)_1px,transparent_1px)] bg-[size:80px_80px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        {/* Section Header */}
        <div
          className={`text-center mb-16 transition-[opacity,transform] duration-700 ${
            isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-[#E8C87A] font-semibold text-sm uppercase tracking-[0.2em]">Study Destinations</span>
          <h2
            className="text-4xl sm:text-5xl font-bold text-white mt-3 mb-5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Choose Your Dream Destination
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto leading-relaxed">
            Explore 20+ study destinations worldwide with expert guidance. We help you find the perfect country and university.
          </p>
        </div>

        {/* Featured Destination Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {destinations.map((dest, index) => (
            <div
              key={dest.country}
              className={`group relative rounded-2xl overflow-hidden cursor-pointer card-tilt transition-[opacity,transform] duration-500 ${
                isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ transitionDelay: `${index * 100}ms` }}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              <div className="relative h-80 overflow-hidden">
                <img
                  src={dest.image}
                  alt={dest.country}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A1628] via-[#0A1628]/40 to-transparent" />
                <div className={`absolute inset-0 bg-[#0A1628]/30 transition-opacity duration-500 ${hoveredIndex === index ? 'opacity-100' : 'opacity-0'}`} />
              </div>

              <div className="absolute bottom-0 left-0 right-0 p-6">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-[#E8C87A]" />
                  <span className="text-[#E8C87A] text-sm font-medium">{dest.universities}</span>
                </div>
                <h3 className="text-2xl font-bold text-white mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  {dest.country}
                </h3>
                <p className="text-white/60 text-sm mb-3">{dest.tagline}</p>
                <div className={`flex flex-wrap gap-2 transition-[opacity,transform] duration-500 ${hoveredIndex === index ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
                  {dest.highlights.map((hl) => (
                    <span key={hl} className="text-xs bg-white/10 backdrop-blur-sm text-white/90 px-3 py-1.5 rounded-full border border-white/10">
                      {hl}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* More Destinations Grid */}
        <div className={`transition-[opacity,transform] duration-700 delay-500 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <h3 className="text-xl font-bold text-white mb-6 text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
            More Destinations We Cover
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {moreDestinations.map((dest) => (
              <div
                key={dest.country}
                className={`group relative rounded-xl overflow-hidden cursor-pointer bg-gradient-to-br ${dest.color} p-4 text-center hover:-translate-y-1 transition-[transform,box-shadow] duration-300 hover:shadow-lg`}
              >
                <p className="text-white font-semibold text-sm">{dest.country}</p>
                <ArrowRight className="w-4 h-4 text-white/50 mx-auto mt-1 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
